import React, { useState } from "react";
import {
  Box,
  Stack,
  Switch,
  TextField,
  FormControlLabel,
  Button,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import {
  School as SchoolIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedSchoolId } from "app/modules/schools/selectors";
import { setFeeSettings as setFeeSettingsInStore } from "app/services/commonSlice";
import {
  getSchoolSetting,
  getFeeCollectionTimeSetting,
  getFeeSetting,
  updatePromotionRule,
  upsertFeeSetting,
  upsertFeeCollectionTimeSetting,
} from "app/services/schoolService/schoolSettings.service";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import CustomLoader from "app/components/common/CustomLoader";

const GeneralSetting = () => {
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const dispatch = useDispatch();

  // Individual state for each setting
  const [promotionSettings, setPromotionSettings] = useState({
    canStudentPromoteWitDues: false,
  });

  const [feeSettings, setFeeSettings] = useState({
    allowExtraDiscountByAccountant: false,
    allowMonthSkipping: false,
  });

  const [feeTimingSettings, setFeeTimingSettings] = useState({
    isEnabled: false,
    feeCollectionCutoffTime: "23:59",
  });

  // Original values for change detection
  const [originalPromotionSettings, setOriginalPromotionSettings] = useState({
    canStudentPromoteWitDues: false,
  });

  const [originalFeeSettings, setOriginalFeeSettings] = useState({
    allowExtraDiscountByAccountant: false,
    allowMonthSkipping: false,
  });

  const [originalFeeTimingSettings, setOriginalFeeTimingSettings] = useState({
    isEnabled: false,
    feeCollectionCutoffTime: "23:59",
  });

  // Change detection functions
  const hasPromotionChanges = () => {
    return (
      promotionSettings.canStudentPromoteWitDues !==
      originalPromotionSettings.canStudentPromoteWitDues
    );
  };

  const hasFeeSettingsChanges = () => {
    const lastYearArrearSettingChanged =
      Object.prototype.hasOwnProperty.call(
        feeSettings,
        "allowCurrentSessionFeeWithLastYearArrear",
      ) !==
        Object.prototype.hasOwnProperty.call(
          originalFeeSettings,
          "allowCurrentSessionFeeWithLastYearArrear",
        ) ||
      (Object.prototype.hasOwnProperty.call(
        feeSettings,
        "allowCurrentSessionFeeWithLastYearArrear",
      ) &&
        feeSettings.allowCurrentSessionFeeWithLastYearArrear !==
          originalFeeSettings.allowCurrentSessionFeeWithLastYearArrear);

    return (
      feeSettings.allowExtraDiscountByAccountant !==
        originalFeeSettings.allowExtraDiscountByAccountant ||
      feeSettings.allowMonthSkipping !==
        originalFeeSettings.allowMonthSkipping ||
      lastYearArrearSettingChanged
    );
  };

  const hasFeeTimingChanges = () => {
    return (
      feeTimingSettings.isEnabled !== originalFeeTimingSettings.isEnabled ||
      feeTimingSettings.feeCollectionCutoffTime !==
        originalFeeTimingSettings.feeCollectionCutoffTime
    );
  };

  // Fetch school settings
  useQuery(
    ["schoolSettings", selectedSchoolId],
    () => getSchoolSetting({ schoolId: selectedSchoolId }),
    {
      enabled: !!selectedSchoolId,
      onSuccess: (response) => {
        if (response?.data) {
          const newPromotionSettings = {
            canStudentPromoteWitDues:
              response.data?.obj?.canStudentPromoteWitDues,
          };
          setPromotionSettings(newPromotionSettings);
          setOriginalPromotionSettings(newPromotionSettings);
        }
      },
    },
  );

  // Fetch fee settings
  const { isLoading: feeSettingsLoading } = useQuery(
    ["feeSettings", selectedSchoolId],
    () => getFeeSetting({ schoolId: selectedSchoolId }),
    {
      enabled: !!selectedSchoolId,
      onSuccess: (response) => {
        if (response?.data?.feeSetting) {
          const fs = response.data.feeSetting;
          const newFeeSettings = {
            allowExtraDiscountByAccountant:
              fs.allowExtraDiscountByAccountant || false,
            allowMonthSkipping: fs.allowMonthSkipping || false,
          };

          if (
            Object.prototype.hasOwnProperty.call(
              fs,
              "allowCurrentSessionFeeWithLastYearArrear",
            )
          ) {
            newFeeSettings.allowCurrentSessionFeeWithLastYearArrear =
              fs.allowCurrentSessionFeeWithLastYearArrear;
          }

          setFeeSettings(newFeeSettings);
          setOriginalFeeSettings(newFeeSettings);

          const storePayload = {
            allowExtraDiscountByAccountant:
              newFeeSettings.allowExtraDiscountByAccountant,
            allowMonthSkipping: newFeeSettings.allowMonthSkipping,
          };
          if (
            Object.prototype.hasOwnProperty.call(
              newFeeSettings,
              "allowCurrentSessionFeeWithLastYearArrear",
            )
          ) {
            storePayload.allowCurrentSessionFeeWithLastYearArrear =
              newFeeSettings.allowCurrentSessionFeeWithLastYearArrear;
          }
          dispatch(setFeeSettingsInStore(storePayload));
        }
      },
      onError: (error) => {
        console.error("Error fetching fee settings:", error);
      },
    },
  );

  // Fetch fee collection time settings
  const { isLoading: feeTimingLoading } = useQuery(
    ["feeCollectionTimeSettings", selectedSchoolId],
    () => getFeeCollectionTimeSetting({ schoolId: selectedSchoolId }),
    {
      enabled: !!selectedSchoolId,
      onSuccess: (response) => {
        if (response?.data) {
          const newFeeTimingSettings = {
            isEnabled: response.data.data.isEnabled || false,
            feeCollectionCutoffTime:
              response.data.data.feeCollectionCutoffTime || "23:59",
          };
          setFeeTimingSettings(newFeeTimingSettings);
          setOriginalFeeTimingSettings(newFeeTimingSettings);
        }
      },
      onError: (error) => {
        console.error("Error fetching fee timing settings:", error);
      },
    },
  );

  // Mutation for updating promotion rules
  const { isLoading: promotionLoading, mutate: updatePromotion } = useMutation(
    (data) => updatePromotionRule(data),
    {
      onSuccess: () => {
        toast.success("Promotion settings updated successfully!");
        setOriginalPromotionSettings({ ...promotionSettings });
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
            "Failed to update promotion settings",
        );
      },
    },
  );

  // Mutation for updating fee settings
  const { isLoading: feeLoading, mutate: updateFeeSetting } = useMutation(
    (data) => upsertFeeSetting(data),
    {
      onSuccess: () => {
        toast.success("Fee settings updated successfully!");
        setOriginalFeeSettings({ ...feeSettings });
        const storePayload = {
          allowExtraDiscountByAccountant:
            feeSettings.allowExtraDiscountByAccountant,
          allowMonthSkipping: feeSettings.allowMonthSkipping,
        };
        if (
          Object.prototype.hasOwnProperty.call(
            feeSettings,
            "allowCurrentSessionFeeWithLastYearArrear",
          )
        ) {
          storePayload.allowCurrentSessionFeeWithLastYearArrear =
            feeSettings.allowCurrentSessionFeeWithLastYearArrear;
        }
        dispatch(setFeeSettingsInStore(storePayload));
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to update fee settings",
        );
      },
    },
  );

  // Mutation for updating fee timing
  const { isLoading: timingLoading, mutate: updateTiming } = useMutation(
    (data) => upsertFeeCollectionTimeSetting(data),
    {
      onSuccess: () => {
        toast.success("Fee timing settings updated successfully!");
        setOriginalFeeTimingSettings({ ...feeTimingSettings });
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to update timing settings",
        );
      },
    },
  );

  const handlePromotionSave = () => {
    updatePromotion({
      canStudentPromoteWitDues: promotionSettings.canStudentPromoteWitDues,
    });
  };

  const handleFeeSave = () => {
    const payload = {
      allowExtraDiscountByAccountant:
        feeSettings.allowExtraDiscountByAccountant,
      allowMonthSkipping: feeSettings.allowMonthSkipping,
    };

    if (
      Object.prototype.hasOwnProperty.call(
        originalFeeSettings,
        "allowCurrentSessionFeeWithLastYearArrear",
      ) ||
      Object.prototype.hasOwnProperty.call(
        feeSettings,
        "allowCurrentSessionFeeWithLastYearArrear",
      )
    ) {
      payload.allowCurrentSessionFeeWithLastYearArrear = Boolean(
        feeSettings.allowCurrentSessionFeeWithLastYearArrear,
      );
    }

    updateFeeSetting(payload);
  };

  const handleTimingSave = () => {
    updateTiming({
      feeCollectionCutoffTime: feeTimingSettings.feeCollectionCutoffTime,
      isEnabled: feeTimingSettings.isEnabled,
    });
  };

  return (
    <>
      <Helmet>
        <title>Settings | General Settings</title>
      </Helmet>

      <CustomLoader
        show={
          feeSettingsLoading ||
          feeTimingLoading ||
          promotionLoading ||
          feeLoading ||
          timingLoading
        }
      />

      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Promotion Settings Card */}
          <Card elevation={2}>
            <CardHeader
              avatar={<SchoolIcon sx={{ color: "#2789fd" }} />}
              title="Promotion Rule"
              subheader="Configure student promotion based on predefined criteria"
              action={
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handlePromotionSave}
                  disabled={promotionLoading || !hasPromotionChanges()}
                  sx={{ bgcolor: "#2789fd" }}
                >
                  {promotionLoading ? <CircularProgress size={20} /> : "Save"}
                </Button>
              }
            />
            <CardContent>
              <Stack spacing={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={promotionSettings.canStudentPromoteWitDues}
                      onChange={(e) =>
                        setPromotionSettings((prev) => ({
                          ...prev,
                          canStudentPromoteWitDues: e.target.checked,
                        }))
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2789fd",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#2789fd" },
                      }}
                    />
                  }
                  label="Can Student Promote With Dues"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Fee Settings Card */}
          <Card elevation={2}>
            <CardHeader
              avatar={<PaymentIcon sx={{ color: "#2789fd" }} />}
              title="Fee Settings"
              subheader="Configure fee settings for the school"
              action={
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleFeeSave}
                  disabled={feeLoading || !hasFeeSettingsChanges()}
                  sx={{ bgcolor: "#2789fd" }}
                >
                  {feeLoading ? <CircularProgress size={20} /> : "Save"}
                </Button>
              }
            />
            <CardContent>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={feeSettings.allowExtraDiscountByAccountant}
                      onChange={(e) =>
                        setFeeSettings((prev) => ({
                          ...prev,
                          allowExtraDiscountByAccountant: e.target.checked,
                        }))
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2789fd",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#2789fd" },
                      }}
                    />
                  }
                  label="Allow Extra Discount By Accountant"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={feeSettings.allowMonthSkipping}
                      onChange={(e) =>
                        setFeeSettings((prev) => ({
                          ...prev,
                          allowMonthSkipping: e.target.checked,
                        }))
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2789fd",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#2789fd" },
                      }}
                    />
                  }
                  label="Allow Month Skipping"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(
                        feeSettings.allowCurrentSessionFeeWithLastYearArrear,
                      )}
                      onChange={(e) =>
                        setFeeSettings((prev) => ({
                          ...prev,
                          allowCurrentSessionFeeWithLastYearArrear:
                            e.target.checked,
                        }))
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2789fd",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#2789fd" },
                      }}
                    />
                  }
                  label="Allow Pay Current Session Fee With Pending Last Year Dues"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Fee Collection Time Settings Card */}
          <Card elevation={2}>
            <CardHeader
              avatar={<ScheduleIcon sx={{ color: "#2789fd" }} />}
              title="Fee Collection Time Settings"
              subheader="Configure fee collection time settings for the school"
              action={
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleTimingSave}
                  disabled={timingLoading || !hasFeeTimingChanges()}
                  sx={{ bgcolor: "#2789fd" }}
                >
                  {timingLoading ? <CircularProgress size={20} /> : "Save"}
                </Button>
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!feeTimingSettings.isEnabled}
                      onChange={(e) =>
                        setFeeTimingSettings((prev) => ({
                          ...prev,
                          isEnabled: !e.target.checked,
                        }))
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2789fd",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#2789fd" },
                      }}
                    />
                  }
                  label="Fee Can be Paid Anytime"
                />

                {/* Time input is always visible but disabled when "Fee Can be Paid Anytime" is ON */}
                <Box sx={{ ml: 4 }}>
                  <TextField
                    label="Fee Collection Cutoff Time"
                    type="time"
                    value={feeTimingSettings.feeCollectionCutoffTime}
                    onChange={(e) =>
                      setFeeTimingSettings((prev) => ({
                        ...prev,
                        feeCollectionCutoffTime: e.target.value,
                      }))
                    }
                    disabled={!feeTimingSettings.isEnabled}
                    sx={{
                      maxWidth: 200,
                      "& .MuiInputBase-input:disabled": {
                        color: "#999",
                        WebkitTextFillColor: "#999",
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText={
                      feeTimingSettings.isEnabled
                        ? "After this time, fee payment will be disabled for the day"
                        : "Fee payment is allowed anytime (time restriction disabled)"
                    }
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </>
  );
};

export default GeneralSetting;
